import { StepCard, StepActions } from '../../Styles/StepLayout';
import React, { useState } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, InputAdornment,
    FormControlLabel, Switch, CircularProgress, Backdrop, Typography
} from "@mui/material";
import Ship from "../../Models/Interfaces/Ship";
import CatchRights from "../../Models/Interfaces/CatchRights";
import StyledButton from "../../Styles/StyledButton";
import StyledTextField from "../../Styles/StyledTextField";
import Title from "../Shared/Title";
import Alert from '@mui/material/Alert';
import { useIsLoadingCatchRights } from "../../Contexts/IsLoadingCatchRightsContext";
import { useSelectedCatchRights } from "../../Contexts/QuotaSelectionContext";
import { useSelectedhowNegativeAflmark } from "../../Contexts/ShowNegativeAflmarkContext";
import { useMobile } from '../../Contexts/MobileContext';
import {
    formatIntegerInput,
    formatNumberWithThousandSeparator,
    parseFormattedNumber
} from '../../Utils/numberFormat';

interface Props {
    companyShip: Ship | null;
    shipTo: Ship | null;
    quotaSelection: CatchRights[] | null;
    setQuotaSelection: React.Dispatch<React.SetStateAction<CatchRights[] | null>>;
    setActiveStep: React.Dispatch<React.SetStateAction<number>>;
    activeStep: number;
    isTransferBetweenCompanies: boolean;
}

const QuotaSelection: React.FC<Props> = ({
    companyShip,
    shipTo,
    quotaSelection,
    setActiveStep,
    activeStep,
}) => {
    const [showAlert, setShowAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const { isLoadingCatchRights } = useIsLoadingCatchRights();
    const { selectedCatchRights, setSelectedCatchRights } = useSelectedCatchRights();
    const { showNegativeAflmark, setShowNegativeAflmark } = useSelectedhowNegativeAflmark();
    const { isMobile } = useMobile();

    const getDefaultFieldValue = (quotaType: number, type: 'quantity' | 'kr') => {
        const selectedValue = selectedCatchRights?.find(x => x.quotaType === quotaType);

        if (!selectedValue) {
            return "";
        }

        if (type === 'quantity') {
            return formatIntegerInput(selectedValue.quantity.toString());
        }

        if (selectedValue.kr === undefined || selectedValue.kr === null) {
            return "";
        }

        return formatIntegerInput(selectedValue.kr.toString());
    };

    const handleInputTyping = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formattedValue = formatIntegerInput(event.target.value);

        event.target.value = formattedValue;
    };

    const handleInputChange = (event: React.FocusEvent<HTMLInputElement>, type: 'quantity' | 'kr', quotaType: number, name: string) => {
        setShowAlert(false);

        const parsedValue = parseFormattedNumber(event.target.value);
        const updatedCatchRights = selectedCatchRights ? [...selectedCatchRights] : [];
        const index = updatedCatchRights.findIndex(x => x.quotaType === quotaType);

        if (parsedValue === null) {
            if (index !== -1) {
                updatedCatchRights.splice(index, 1);
            }
            event.target.value = "";
            setSelectedCatchRights(updatedCatchRights);
            return;
        }

        const value = Math.trunc(parsedValue);

        if (index === -1) {
            addNewCatchRight(updatedCatchRights, quotaType, name, value, type);
        } else {
            updateExistingCatchRight(updatedCatchRights, index, value, type);
        }

        event.target.value = formatIntegerInput(value.toString());

        setSelectedCatchRights(updatedCatchRights);
    };

    const addNewCatchRight = (updatedCatchRights: CatchRights[], quotaType: number, name: string, value: number, type: 'quantity' | 'kr') => {
        const newCatchRight: CatchRights = {
            quotaType,
            name,
            quantity: type === 'quantity' ? value : 0,
            kr: type === 'kr' ? value : 0,
            unit: quotaSelection?.find(x => x.quotaType === quotaType)?.unit || 1
        };
        updatedCatchRights.push(newCatchRight);
    };

    const updateExistingCatchRight = (updatedCatchRights: CatchRights[], index: number, value: number, type: 'quantity' | 'kr') => {
        if (type === 'quantity') {
            updatedCatchRights[index].quantity = value;
        } else {
            updatedCatchRights[index].kr = value;
        }
    };

    const validateQuotaSelection = () => {
        if (!selectedCatchRights) return;

        selectedCatchRights.forEach((quotaItem, index) => {
            if (quotaItem.quantity === 0 && quotaItem.kr === 0) {
                selectedCatchRights.splice(index, 1);
            }
        });

        if (selectedCatchRights.some(quotaItem => quotaItem.quantity === 0 || quotaItem.quantity === null)) {
            setErrorMessage("Magn verður að vera stærra en 0.");
            setShowAlert(true);
            return;
        }

        if (selectedCatchRights.some(quotaItem => Math.trunc(quotaItem.quantity).toString().length > 8)) {
            setErrorMessage("Magn má ekki vera meira en 8 tölustafir.");
            setShowAlert(true);
            return;
        }

        const hasInvalidPrice = selectedCatchRights.some(quotaItem => {
            if (!quotaItem.kr) {
                return false;
            }

            return Math.trunc(quotaItem.kr).toString().length > 5;
        });

        if (hasInvalidPrice) {
            setErrorMessage("Verð má aðeins innihalda 5 tölustafi.");
            setShowAlert(true);
            return;
        }

        const updatedCatchRights = selectedCatchRights.filter(quotaItem =>
            quotaItem.quantity > 0
        );
        setSelectedCatchRights(updatedCatchRights);
        setActiveStep(activeStep + 1);
    }

    const handleKeyDown = (type: 'quantity' | 'kr') => (event: React.KeyboardEvent<HTMLInputElement>) => {
        const invalidKeys = ["-", "+", "e", "E"];
        invalidKeys.push(".", ",");
        if (invalidKeys.includes(event.key)) {
            event.preventDefault();
        }
    };

    return (
        <StepCard>
            {isLoadingCatchRights && (
                <Backdrop style={{ zIndex: 1300, color: '#fff' }} open={isLoadingCatchRights}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
            <Title title="Magn millifærslu" />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, px: 2, py: 1.5, bgcolor: 'background.default', borderRadius: 1.5 }}>
                <Typography variant="body2" sx={{ flex: '1 1 200px' }}>
                    <Box component="span" color="text.secondary" sx={{ mr: 1 }}>Frá</Box>
                    <strong>{companyShip?.shipNumber}</strong> {companyShip?.name} {companyShip?.district?.split(' (')[0]}
                </Typography>
                <Typography variant="body2" sx={{ flex: '1 1 200px' }}>
                    <Box component="span" color="text.secondary" sx={{ mr: 1 }}>Til</Box>
                    <strong>{shipTo?.shipNumber}</strong> {shipTo?.name} {shipTo?.district?.split(' (')[0]}
                </Typography>
            </Box>
            {showAlert && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMessage}
                </Alert>
            )}
            {(quotaSelection && quotaSelection.length === 0) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Engar aflaheimildir fundust á skipinu sem valið var.
                </Alert>
            )}
            {!isLoadingCatchRights && quotaSelection && quotaSelection.length > 0 && !quotaSelection.some(quota => quota.quantity > 0) && !showNegativeAflmark && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Engar kvótategundir með jákvætt aflamark. Kveiktu á „Sýna neikvætt aflmark“ til að sjá aðrar stöður.
                </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={showNegativeAflmark}
                            onChange={() => setShowNegativeAflmark(!showNegativeAflmark)}
                            name="showNegativeAflmark"
                            color="primary"
                        />
                    }
                    label="Sýna neikvætt aflmark"
                />
            </Box>

            {isMobile ? (
                <Box>
                    {quotaSelection?.filter(x => x.quantity > 0).map((quota) => (
                        <Paper key={quota.quotaType} variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>{quota.name}</Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Aflamark: {`${formatNumberWithThousandSeparator(quota.quantity)} ${quota.unit === 1 ? 'kg' : 'tonn'}`}
                            </Typography>
                            <StyledTextField
                                type="text"
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                fullWidth
                                onChange={handleInputTyping}
                                InputProps={{
                                    onBlur: (event: React.FocusEvent<HTMLInputElement>) => handleInputChange(event, 'quantity', quota.quotaType, quota.name),
                                    onKeyDown: handleKeyDown('quantity'),
                                    endAdornment: <InputAdornment position="end">{quota.unit === 1 ? 'kg' : 'tonn'}</InputAdornment>
                                }}
                                inputProps={{
                                    inputMode: 'numeric', 'aria-label': 'Flutt magn - ' + quota.name
                                }}
                                label="Flutt magn"
                                defaultValue={getDefaultFieldValue(quota.quotaType, 'quantity')}
                                sx={{ mb: 2 }}
                            />
                            <StyledTextField
                                type="text"
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                fullWidth
                                onChange={handleInputTyping}
                                InputProps={{
                                    onBlur: (event: React.FocusEvent<HTMLInputElement>) => handleInputChange(event, 'kr', quota.quotaType, quota.name),
                                    onKeyDown: handleKeyDown('kr'),
                                    endAdornment: <InputAdornment position="end">kr</InputAdornment>
                                }}
                                inputProps={{
                                    inputMode: 'numeric', 'aria-label': 'Einingarverð - ' + quota.name
                                }}
                                label="Einingarverð"
                                defaultValue={getDefaultFieldValue(quota.quotaType, 'kr')}
                            />
                        </Paper>
                    ))}
                    {showNegativeAflmark && quotaSelection?.filter(x => x.quantity <= 0).map((quota) => (
                        <Paper key={quota.quotaType} variant="outlined" sx={{ p: 2.5, mb: 2, bgcolor: 'primary.light' }}>
                            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>{quota.name}</Typography>
                            <Typography variant="body2" sx={{ marginBottom: '10px', color: 'primary.main' }}>
                                Aflamark: {`${formatNumberWithThousandSeparator(quota.quantity)} ${quota.unit === 1 ? 'kg' : 'tonn'}`}
                            </Typography>
                            <StyledTextField
                                type="text"
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                fullWidth
                                onChange={handleInputTyping}
                                InputProps={{
                                    onBlur: (event: React.FocusEvent<HTMLInputElement>) => handleInputChange(event, 'quantity', quota.quotaType, quota.name),
                                    onKeyDown: handleKeyDown('quantity'),
                                    endAdornment: <InputAdornment position="end">{quota.unit === 1 ? 'kg' : 'tonn'}</InputAdornment>
                                }}
                                inputProps={{
                                    inputMode: 'numeric', 'aria-label': 'Flutt magn - ' + quota.name
                                }}
                                label="Flutt magn"
                                defaultValue={getDefaultFieldValue(quota.quotaType, 'quantity')}
                                sx={{ mb: 2 }}
                            />
                            <StyledTextField
                                type="text"
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                fullWidth
                                onChange={handleInputTyping}
                                InputProps={{
                                    onBlur: (event: React.FocusEvent<HTMLInputElement>) => handleInputChange(event, 'kr', quota.quotaType, quota.name),
                                    onKeyDown: handleKeyDown('kr'),
                                    endAdornment: <InputAdornment position="end">kr</InputAdornment>
                                }}
                                inputProps={{
                                    inputMode: 'numeric', 'aria-label': 'Einingarverð - ' + quota.name
                                }}
                                label="Einingarverð"
                                defaultValue={getDefaultFieldValue(quota.quotaType, 'kr')}
                            />
                        </Paper>
                    ))}
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 680, tableLayout: 'fixed' }} aria-label="Kvótategundir og magn millifærslu">
                        <TableHead>
                            <TableRow>
                                <TableCell>Fisktegund</TableCell>
                                <TableCell align="right">Aflamark</TableCell>
                                <TableCell align="right">Flutt magn</TableCell>
                                <TableCell align="right">Einingarverð</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {quotaSelection?.filter(x => x.quantity > 0).map((quota, index) => (
                                <TableRow key={quota.quotaType} hover sx={{ bgcolor: selectedCatchRights?.some(right => right.quotaType === quota.quotaType && right.quantity > 0) ? 'primary.light' : undefined }}>
                                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>{quota.name}</TableCell>
                                    <TableCell align="right">{`${formatNumberWithThousandSeparator(quota.quantity)} ${quota.unit === 1 ? 'kg' : 'tonn'}`}</TableCell>
                                    <TableCell align="right">
                                        <StyledTextField
                                            type="text"
                                            id={`${quota.quotaType}-${index}-quantity`}
                                            variant="outlined"
                                            fullWidth
                                            onChange={handleInputTyping}
                                            InputProps={{
                                                onBlur: (event: React.FocusEvent<HTMLInputElement>) => handleInputChange(event, 'quantity', quota.quotaType, quota.name),
                                                onKeyDown: handleKeyDown('quantity'),
                                                endAdornment: <InputAdornment position="end">{quota.unit === 1 ? 'kg' : 'tonn'}</InputAdornment>
                                            }}
                                            inputProps={{
                                                inputMode: 'numeric', 'aria-label': 'Flutt magn - ' + quota.name
                                            }}
                                            defaultValue={getDefaultFieldValue(quota.quotaType, 'quantity')}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <StyledTextField
                                            type="text"
                                            variant="outlined"
                                            fullWidth
                                            onChange={handleInputTyping}
                                            InputProps={{
                                                onBlur: (event: React.FocusEvent<HTMLInputElement>) => handleInputChange(event, 'kr', quota.quotaType, quota.name),
                                                onKeyDown: handleKeyDown('kr'),
                                                endAdornment: <InputAdornment position="end">kr</InputAdornment>
                                            }}
                                            inputProps={{
                                                inputMode: 'numeric', 'aria-label': 'Einingarverð - ' + quota.name
                                            }}
                                            defaultValue={getDefaultFieldValue(quota.quotaType, 'kr')}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {showNegativeAflmark && quotaSelection?.filter(x => x.quantity <= 0).map((quota, index) => (
                                <TableRow key={quota.quotaType} hover sx={{ bgcolor: selectedCatchRights?.some(right => right.quotaType === quota.quotaType && right.quantity > 0) ? 'primary.light' : undefined }}>
                                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>{quota.name}</TableCell>
                                    <TableCell align="right" sx={{ color: 'primary.main' }}>{`${formatNumberWithThousandSeparator(quota.quantity)} ${quota.unit === 1 ? 'kg' : 'tonn'}`}</TableCell>
                                    <TableCell align="right">
                                        <StyledTextField
                                            type="text"
                                            id={`${quota.quotaType}-${index}-quantity`}
                                            variant="outlined"
                                            fullWidth
                                            onChange={handleInputTyping}
                                            InputProps={{
                                                onBlur: (event: React.FocusEvent<HTMLInputElement>) => handleInputChange(event, 'quantity', quota.quotaType, quota.name),
                                                onKeyDown: handleKeyDown('quantity'),
                                                endAdornment: <InputAdornment position="end">{quota.unit === 1 ? 'kg' : 'tonn'}</InputAdornment>
                                            }}
                                            inputProps={{
                                                inputMode: 'numeric', 'aria-label': 'Flutt magn - ' + quota.name
                                            }}
                                            defaultValue={getDefaultFieldValue(quota.quotaType, 'quantity')}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <StyledTextField
                                            type="text"
                                            variant="outlined"
                                            fullWidth
                                            onChange={handleInputTyping}
                                            InputProps={{
                                                onBlur: (event: React.FocusEvent<HTMLInputElement>) => handleInputChange(event, 'kr', quota.quotaType, quota.name),
                                                onKeyDown: handleKeyDown('kr'),
                                                endAdornment: <InputAdornment position="end">kr</InputAdornment>
                                            }}
                                            inputProps={{
                                                inputMode: 'numeric', 'aria-label': 'Einingarverð - ' + quota.name
                                            }}
                                            defaultValue={getDefaultFieldValue(quota.quotaType, 'kr')}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <StepActions>
                <StyledButton
                    variant="outlined"
                    onClick={() => {
                        setActiveStep(activeStep - 1);
                    }}
                    fullWidth={false}
                >
                    Til baka
                </StyledButton>
                <StyledButton
                    variant="contained"
                    onClick={validateQuotaSelection}
                    disabled={
                        !selectedCatchRights ||
                        selectedCatchRights.length === 0 ||
                        selectedCatchRights.every(right => right.quantity === 0 && right.kr === 0)}
                    fullWidth={false}
                >
                    Áfram
                </StyledButton>
            </StepActions>

        </StepCard>
    );
}

export default QuotaSelection;

